import { useState, useEffect } from 'react';
import { MenuItem } from '../types';
import { useApp } from '../context/AppContext';
import type { MenuItemSizeVariant } from '../types';

interface MenuItemDetailModalProps {
  visible: boolean;
  onClose: () => void;
  item: MenuItem | null;
  readOnly?: boolean;
}

// Imágenes por defecto atractivas para diferentes tipos de comida
const defaultFoodImages = [
  '🍕', '🍔', '🥗', '🍜', '🍣', '🥩', '🍗', '🐟', '🍤', '🥘', 
  '🍲', '🥣', '🥙', '🌮', '🌯', '🥪', '🥐', '🍞', '🍰', '🍮', 
  '🍦', '🍨', '🍩', '🍪', '🍫', '🍬', '🍭', '🍯', '☕', '🍵', 
  '🥤', '🍺', '🍷', '🍸', '🍹', '🥂', '🥃', '🍾'
];

// Función para obtener una imagen por defecto basada en el nombre del plato
const getDefaultFoodEmoji = (itemName: string): string => {
  const name = itemName.toLowerCase();
  
  // Mapeo específico por tipo de comida
  if (name.includes('pizza') || name.includes('pizz')) return '🍕';
  if (name.includes('hamburguesa') || name.includes('burger') || name.includes('hamburger')) return '🍔';
  if (name.includes('ensalada') || name.includes('salad')) return '🥗';
  if (name.includes('pasta') || name.includes('spaghetti') || name.includes('fettuccine')) return '🍜';
  if (name.includes('sushi') || name.includes('maki') || name.includes('roll')) return '🍣';
  if (name.includes('carne') || name.includes('steak') || name.includes('beef')) return '🥩';
  if (name.includes('pollo') || name.includes('chicken')) return '🍗';
  if (name.includes('pescado') || name.includes('fish') || name.includes('salmon')) return '🐟';
  if (name.includes('camarón') || name.includes('shrimp') || name.includes('prawn')) return '🍤';
  if (name.includes('sopa') || name.includes('soup')) return '🥘';
  if (name.includes('taco') || name.includes('burrito')) return '🌮';
  if (name.includes('sandwich') || name.includes('sándwich')) return '🥪';
  if (name.includes('pan') || name.includes('bread')) return '🍞';
  if (name.includes('postre') || name.includes('dessert') || name.includes('cake')) return '🍰';
  if (name.includes('helado') || name.includes('ice cream')) return '🍦';
  if (name.includes('café') || name.includes('coffee')) return '☕';
  if (name.includes('té') || name.includes('tea')) return '🍵';
  if (name.includes('bebida') || name.includes('drink') || name.includes('jugo')) return '🥤';
  if (name.includes('cerveza') || name.includes('beer')) return '🍺';
  if (name.includes('vino') || name.includes('wine')) return '🍷';
  if (name.includes('cocktail') || name.includes('cóctel')) return '🍸';
  
  // Si no coincide con nada específico, usar un emoji aleatorio basado en el hash del nombre
  const hash = itemName.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  return defaultFoodImages[Math.abs(hash) % defaultFoodImages.length];
};

// Función para obtener el color de fondo basado en el emoji
const getBackgroundColorForEmoji = (emoji: string): string => {
  const colorMap: { [key: string]: string } = {
    '🍕': '#FFE4E1', // Rosa claro para pizza
    '🍔': '#FFF8DC', // Amarillo claro para hamburguesas
    '🥗': '#F0FFF0', // Verde claro para ensaladas
    '🍜': '#FFF5EE', // Naranja claro para pasta
    '🍣': '#F0F8FF', // Azul claro para sushi
    '🥩': '#FFE4E1', // Rosa claro para carnes
    '🍗': '#FFF8DC', // Amarillo claro para pollo
    '🐟': '#E6F3FF', // Azul muy claro para pescado
    '🍤': '#FFF0F5', // Rosa muy claro para mariscos
    '🥘': '#FFF5EE', // Naranja claro para sopas
    '🌮': '#FFFACD', // Amarillo claro para tacos
    '🥪': '#F5F5DC', // Beige claro para sandwiches
    '🍞': '#FFF8DC', // Amarillo claro para pan
    '🍰': '#FFF0F5', // Rosa muy claro para postres
    '🍦': '#F0F8FF', // Azul claro para helado
    '☕': '#F5F5DC', // Beige claro para café
    '🍵': '#F0FFF0', // Verde claro para té
    '🥤': '#E6F3FF', // Azul muy claro para bebidas
    '🍺': '#FFFACD', // Amarillo claro para cerveza
    '🍷': '#FFE4E1', // Rosa claro para vino
    '🍸': '#FFF0F5', // Rosa muy claro para cócteles
  };
  
  return colorMap[emoji] || '#F8F9FA'; // Gris claro por defecto
};

export default function MenuItemDetailModal({ visible, onClose, item, readOnly }: MenuItemDetailModalProps) {
  const { addToCart } = useApp();
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<MenuItemSizeVariant | null>(null);

  const handleAddToCart = () => {
    if (!item) return;
    
    if (readOnly) return;
    if (quantity > 0) {
      // Ajustar precio por tamaño si aplica
      let price = item.price;
      if (selectedSize) {
        if (typeof selectedSize.price === 'number') price = selectedSize.price;
        else if (typeof selectedSize.priceDelta === 'number') price = item.price + selectedSize.priceDelta;
      }
      const sizedItem = { ...item, price } as any;
      addToCart(
        sizedItem,
        quantity,
        specialInstructions.trim() || undefined,
        selectedSize ? selectedSize.name : undefined
      );
      setQuantity(1);
      setSpecialInstructions('');
      setSelectedSize(null);
      
      // Cerrar el modal después de agregar al carrito
      onClose();
      
      // Mostrar notificación sutil
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300';
      notification.innerHTML = `
        <div class="flex items-center space-x-2">
          <span class="text-xl">✅</span>
          <span class="font-semibold">¡Agregado al carrito!</span>
        </div>
        <div class="text-sm opacity-90">${item.name}</div>
      `;
      document.body.appendChild(notification);
      
      // Animar entrada
      setTimeout(() => {
        notification.classList.remove('translate-x-full');
      }, 100);
      
      // Animar salida y remover
      setTimeout(() => {
        notification.classList.add('translate-x-full');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const getStatusColor = () => {
    return item?.available ? '#4ADE80' : '#FCA5A5';
  };

  // Preparar imágenes para el carousel
  const getImagesForCarousel = () => {
    if (!item) return [];
    
    const images: Array<{ type: 'backend' | 'emoji'; imageUrl?: string; emoji?: string }> = [];
    
    // Agregar imágenes del backend si existen
    if (item.images && item.images.length > 0) {
      item.images.forEach(img => {
        // Manejar tanto strings como objetos MenuImage
        const imageUrl = typeof img === 'string' ? img : img.imageUrl;
        if (imageUrl) {
          images.push({ type: 'backend', imageUrl });
        }
      });
    }
    
    // Si no hay imágenes del backend, agregar emoji por defecto
    if (images.length === 0) {
      images.push({ type: 'emoji', emoji: getDefaultFoodEmoji(item.name) });
    }
    
    return images;
  };

  const carouselImages = getImagesForCarousel();

  // Resetear estado cuando se abre el modal
  useEffect(() => {
    if (visible) {
      setQuantity(1);
      setSpecialInstructions('');
      setCurrentImageIndex(0);
      if (!readOnly && item?.sizes && item.sizes.length > 0) {
        setSelectedSize(item.sizes[0]);
      } else {
        setSelectedSize(null);
      }
    }
  }, [visible, item?.id, readOnly]);

  if (!item || !visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="relative bg-white w-full sm:max-w-lg max-h-[90vh] sm:max-h-[80vh] overflow-hidden shadow-xl rounded-t-2xl sm:rounded-2xl flex flex-col">
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          title="Cerrar"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white"
        >
          ✕
        </button>

        <div className="flex-1 overflow-y-auto">
          <div className="p-5 pt-12 sm:pt-5">
            {/* Carousel de imágenes */}
            <div className="relative mb-4">
              <div className="relative h-48 rounded-xl overflow-hidden">
                {carouselImages[currentImageIndex].type === 'emoji' ? (
                  <div 
                    className="w-full h-full flex items-center justify-center text-6xl"
                    style={{ backgroundColor: getBackgroundColorForEmoji(carouselImages[currentImageIndex].emoji!) }}
                  >
                    {carouselImages[currentImageIndex].emoji}
                  </div>
                ) : (
                  <img
                    src={carouselImages[currentImageIndex].imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              
              {/* Indicadores de página */}
              {carouselImages.length > 1 && (
                <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                  {carouselImages.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentImageIndex ? 'bg-[#22B6B2]' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Navegación del carousel */}
              {carouselImages.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-black bg-opacity-50 text-white flex items-center justify-center"
                    onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : carouselImages.length - 1)}
                  >
                    ‹
                  </button>
                  <button
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-black bg-opacity-50 text-white flex items-center justify-center"
                    onClick={() => setCurrentImageIndex(prev => prev < carouselImages.length - 1 ? prev + 1 : 0)}
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {/* Información del plato */}
            <div className="space-y-4">
              {/* Header con nombre y precio */}
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-[#2C4358]">{item.name}</h2>
                <span className="text-2xl font-bold text-[#22B6B2]">{formatPrice(item.price)}</span>
              </div>

              {/* Estado de disponibilidad */}
              <div>
                <span 
                  className="px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: getStatusColor() }}
                >
                  {item.available ? 'Disponible' : 'No disponible'}
                </span>
              </div>

              {/* Descripción */}
              <div>
                <h3 className="text-lg font-bold text-[#2C4358] mb-2">Descripción</h3>
                <p className="text-base leading-6 text-[#85929e]">{item.description}</p>
              </div>

              {/* Información adicional */}
              <div className="space-y-3">
                {item.preparationTime && (
                  <div className="flex items-center">
                    <span className="text-lg mr-2">⏱️</span>
                    <span className="text-sm text-[#85929e]">
                      Tiempo de preparación: {item.preparationTime} minutos
                    </span>
                  </div>
                )}

                {/* Alérgenos */}
                {item.allergens && item.allergens.length > 0 && (
                  <div>
                    <h4 className="text-base font-bold text-gray-600 mb-2">⚠️ Alérgenos</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.allergens.map((allergen, index) => (
                        <span 
                          key={index} 
                          className="bg-gray-100 px-3 rounded-full text-xs font-bold text-gray-700 border border-gray-200"
                        >
                          {allergen}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Tamaños */}
              {!readOnly && item.sizes && item.sizes.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-[#2C4358] mb-2">Tamaño</h3>
                  <div className="flex flex-wrap gap-2">
                    {item.sizes.map((s, idx) => {
                      const effectivePrice = typeof s.price === 'number' ? s.price : (item.price + (s.priceDelta || 0));
                      const active = selectedSize?.name === s.name;
                      return (
                        <button
                          key={idx}
                          onClick={() => setSelectedSize(s)}
                          className={`px-3 py-2 rounded-xl border text-sm font-bold ${active ? 'bg-[#22B6B2] text-white border-[#22B6B2]' : 'bg-white text-[#2C4358] border-[#22B6B2]/30 hover:border-[#22B6B2]'}`}
                        >
                          {s.name} · ${effectivePrice.toFixed(2)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Cantidad e instrucciones especiales */}
              {!readOnly && (
                <div className="border-t border-[#f7ebd6] pt-4">
                  <h3 className="text-xl font-bold text-[#2C4358] mb-4">Agregar al carrito</h3>
                  
                  <div className="mb-5">
                    <label className="block text-lg font-bold text-[#2C4358] mb-2">Cantidad:</label>
                    <div className="flex items-center justify-center space-x-5">
                      <button
                        className="w-12 h-12 rounded-full bg-[#f7ebd6] text-[#2C4358] text-2xl font-bold flex items-center justify-center hover:bg-[#f0e0c0] transition-colors border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      >
                        -
                      </button>
                      <span className="text-2xl font-bold text-[#2C4358] min-w-[40px] text-center">
                        {quantity}
                      </span>
                      <button
                        className="w-12 h-12 rounded-full bg-[#f7ebd6] text-[#2C4358] text-2xl font-bold flex items-center justify-center hover:bg-[#f0e0c0] transition-colors border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
                        onClick={() => setQuantity(quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="block text-lg font-bold text-[#2C4358] mb-2">
                      Instrucciones especiales:
                    </label>
                    <textarea
                      className="w-full border border-[#f7ebd6] rounded-xl p-4 text-base text-[#2C4358] bg-white min-h-20 resize-none"
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                      placeholder="Ej: Sin cebolla, bien cocido..."
                      rows={3}
                    />
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-[#f7ebd6]">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-[#2C4358]">Total:</span>
                      <span className="text-2xl font-bold text-[#22B6B2]">
                        {formatPrice(((selectedSize && (typeof selectedSize.price === 'number' ? selectedSize.price : item.price + (selectedSize.priceDelta || 0))) || item.price) * quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Botón de acción */}
        {!readOnly && (
          <div
            className="px-5 py-3 border-t border-[#f7ebd6] bg-white"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          >
            <button
              className={`w-full py-4 rounded-xl text-lg font-bold transition-colors border-2 ${
                item.available 
                  ? 'bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] border-[#f7ebd6]/50 hover:border-[#f0e0c0]' 
                  : 'bg-[#6b7280] text-white cursor-not-allowed border-[#6b7280]/50'
              }`}
              onClick={() => item.available && handleAddToCart()}
              disabled={!item.available}
            >
              {item.available ? 'Agregar al carrito' : 'No disponible'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 